import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDomainType } from '@/hooks/useDomainType';
import { isPreviewDeploymentHost } from '@/utils/deploymentHost';
import { MOBILE_MAX_WIDTH, readIsMobileLayout } from '@/utils/isMobileLayout';

export type DeviceMode = 'desktop' | 'mobile' | 'kiosk';

const DeviceModeContext = createContext<DeviceMode | null>(null);

/**
 * Nguồn chân lý 3 design profile (body[data-mode]):
 * 1. Viewport hẹp (<768px) → `mobile` (BottomNav, ẩn top tabs) — mọi domain, kể cả kiosk.
 *    Phone + "Desktop site" (rộng giả) cũng về `mobile` qua device-width / outer / screen.
 * 2. Viewport rộng + domain kiosk (API) + không phải Pages preview → `kiosk`.
 * 3. Còn lại → `desktop`.
 *
 * Preview (*.pages.dev): không ép kiosk layout để QA mobile/desktop trên Cloudflare Pages.
 */
export function DeviceModeProvider({ children }: { children: React.ReactNode }) {
  const domain = useDomainType();
  const [narrow, setNarrow] = useState(readIsMobileLayout);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH - 1}px)`);
    const update = () => setNarrow(readIsMobileLayout());
    update();
    mq.addEventListener('change', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      mq.removeEventListener('change', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  const kioskHardwareLayout =
    domain === 'kiosk' && !isPreviewDeploymentHost();

  const mode: DeviceMode = narrow
    ? 'mobile'
    : kioskHardwareLayout
      ? 'kiosk'
      : 'desktop';

  useEffect(() => {
    document.body.dataset.mode = mode;
    // Phone + Desktop site: layout width ≥768 so @media bottom-nav height won't apply.
    document.documentElement.style.setProperty(
      '--wf-bottom-nav-h',
      mode === 'mobile' ? '56px' : '0px'
    );
    return () => {
      delete document.body.dataset.mode;
      document.documentElement.style.removeProperty('--wf-bottom-nav-h');
    };
  }, [mode]);

  return <DeviceModeContext.Provider value={mode}>{children}</DeviceModeContext.Provider>;
}

export function useDeviceMode(): DeviceMode {
  const ctx = useContext(DeviceModeContext);
  if (!ctx) throw new Error('useDeviceMode must be used inside <DeviceModeProvider>');
  return ctx;
}
